export function Card({ children, className = "", ...rest }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl shadow-sm ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = "default", testId }) {
  const tones = {
    default: "text-slate-900",
    positive: "text-emerald-700",
    negative: "text-rose-600",
    warn: "text-amber-700",
  };
  return (
    <Card className="p-5 min-w-0">
      <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-3 truncate">
        {label}
      </div>
      <div className={`heading text-xl md:text-2xl font-semibold truncate ${tones[tone]}`} data-testid={testId} title={typeof value === "string" ? value : undefined}>
        {value}
      </div>
      {hint && <div className="text-xs text-slate-500 mt-2 truncate">{hint}</div>}
    </Card>
  );
}

export function Badge({ tone = "neutral", children, ...rest }) {
  const tones = {
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    neutral: "bg-slate-100 text-slate-700 border-slate-200",
    info: "bg-sky-50 text-sky-700 border-sky-200",
  };
  return (
    <span {...rest} className={`inline-flex text-[11px] px-2 py-0.5 rounded-full border font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
      <div>
        <h1 className="heading text-2xl md:text-3xl font-semibold text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Button({ variant = "primary", className = "", children, ...props }) {
  const styles = {
    primary: "bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50",
    secondary: "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50",
    danger: "bg-rose-600 text-white hover:bg-rose-700",
    ghost: "text-slate-600 hover:bg-slate-100",
  };
  return (
    <button
      {...props}
      className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 ${styles[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export function Input({ label, error, className = "", testId, ...props }) {
  return (
    <label className={`block ${className}`}>
      {label && <div className="text-xs font-medium text-slate-600 mb-1.5">{label}</div>}
      <input
        {...props}
        data-testid={testId}
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none disabled:bg-slate-50"
      />
      {error && <div className="text-xs text-rose-600 mt-1">{error}</div>}
    </label>
  );
}

export function Select({ label, children, className = "", testId, ...props }) {
  return (
    <label className={`block ${className}`}>
      {label && <div className="text-xs font-medium text-slate-600 mb-1.5">{label}</div>}
      <select
        {...props}
        data-testid={testId}
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 bg-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
      >
        {children}
      </select>
    </label>
  );
}

export function Modal({ open, onClose, title, children, maxWidth = "max-w-lg" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-xl w-full ${maxWidth} max-h-[90vh] my-auto overflow-hidden flex flex-col fade-up`} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
          <h3 className="heading text-lg font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="modal-close">✕</button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 min-h-0">{children}</div>
      </div>
    </div>
  );
}

export function EmptyState({ title, subtitle, action }) {
  return (
    <div className="text-center py-16">
      <div className="heading text-lg font-semibold text-slate-700">{title}</div>
      {subtitle && <div className="text-sm text-slate-500 mt-1">{subtitle}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
